import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from './lib/api';
import {
  Typography, Grid, Card, CardContent, Box, Paper, Divider,
  useTheme, useMediaQuery
} from '@mui/material';
import DateRangePickerField from './components/DateRangePickerField';
import {
  TrendingUp, TrendingDown, AccountBalance, Business,
  Receipt, People, Assessment
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { el } from 'date-fns/locale';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line
} from 'recharts';

// Modern gradient color palette - soft pastels like the reference image
const COLORS = [
  '#06b6d4', // cyan
  '#10b981', // emerald  
  '#84cc16', // lime
  '#eab308', // yellow
  '#f97316', // orange
  '#f472b6', // pink
  '#a78bfa', // violet
  '#60a5fa', // blue
];

// Gradient definitions for charts
const GradientDefs = () => (
  <defs>
    {/* Donut gradient - matches reference image */}
    <linearGradient id="donutGradient" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.9} />
      <stop offset="25%" stopColor="#10b981" stopOpacity={0.9} />
      <stop offset="50%" stopColor="#84cc16" stopOpacity={0.9} />
      <stop offset="75%" stopColor="#eab308" stopOpacity={0.9} />
      <stop offset="100%" stopColor="#f97316" stopOpacity={0.9} />
    </linearGradient>
    
    {/* Revenue gradient */}
    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
      <stop offset="100%" stopColor="#059669" stopOpacity={0.9} />
    </linearGradient>
    
    {/* Expense gradient */}
    <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#f97316" stopOpacity={0.9} />
      <stop offset="100%" stopColor="#ea580c" stopOpacity={0.9} />
    </linearGradient>
    
    {/* Bar chart gradient */}
    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.9} />
      <stop offset="100%" stopColor="#6366f1" stopOpacity={0.9} />
    </linearGradient>

    {/* Soft shadows */}
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="4" stdDeviation="8" floodOpacity="0.15"/>
    </filter>
  </defs>
);

// Custom tooltip style
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 2,
        p: 1.5,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
          {label || payload[0]?.name}
        </Typography>
        {payload.map((item, index) => (
          <Typography key={index} variant="body2" sx={{ color: item.color }}>
            {formatter ? formatter(item.value) : item.value}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

// Helper function to format date in local timezone (avoids UTC conversion issues)
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Permission imports
import { usePermissions } from './hooks/usePermissions';
import { PERMISSIONS } from './utils/permissions';
import { AccessDenied } from './components/PermissionGate';

export default function FinancialOverview() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Permission checks
  const { can } = usePermissions();
  const canView = can(PERMISSIONS.FINANCIAL_VIEW);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());

  // Summary data
  const [revenue, setRevenue] = useState({ total_revenue: 0, total_cash: 0, total_card: 0 });
  const [invoiceExpenses, setInvoiceExpenses] = useState({ total_amount: 0, paid_amount: 0, pending_amount: 0 });
  const [generalExpenses, setGeneralExpenses] = useState({ total_amount: 0, paid_amount: 0, pending_amount: 0 });
  const [payroll, setPayroll] = useState({ total: 0, paid: 0, unpaid: 0 });
  const [categoryExpenses, setCategoryExpenses] = useState([]);
  const [supplierCategoryExpenses, setSupplierCategoryExpenses] = useState([]);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  // Auto-generate recurring expenses on mount
  useEffect(() => {
    generateRecurringExpenses();
  }, []);

  const generateRecurringExpenses = async () => {
    try {
      await fetchWithAuth('/api/recurring-expenses/generate', { method: 'POST' });
    } catch (error) {
      console.error('Error generating recurring expenses:', error);
    }
  };

  const fetchData = async () => {
    // Use local timezone formatting to avoid UTC conversion issues
    const start = formatDateLocal(startDate);
    const end = formatDateLocal(endDate);

    console.log('🔄 FinancialOverview fetchData called with:', { start, end });

    try {
      // Fetch revenue
      const revenueRes = await fetchWithAuth(`/api/revenue/summary/stats?startDate=${start}&endDate=${end}`);
      const revenueData = await revenueRes.json();
      setRevenue(revenueData);

      // Fetch invoice expenses (suppliers)
      try {
        const invoiceRes = await fetchWithAuth(`/api/supplier-invoices/summary/stats?startDate=${start}&endDate=${end}`);
        if (invoiceRes.ok) {
          const invoiceData = await invoiceRes.json();
          setInvoiceExpenses(invoiceData);
        }
      } catch (e) {
        console.warn('Could not fetch invoice expenses:', e);
      }

      // Fetch general expenses
      const generalRes = await fetchWithAuth(`/api/general-expenses/summary/stats?startDate=${start}&endDate=${end}`);
      const generalData = await generalRes.json();
      setGeneralExpenses(generalData);

      // Fetch category breakdown from both sources
      let invoiceCatData = [];
      let generalCatData = [];
      
      try {
        const invoiceCatRes = await fetchWithAuth(`/api/supplier-invoices/summary/by-category?startDate=${start}&endDate=${end}`);
        if (invoiceCatRes.ok) {
          invoiceCatData = await invoiceCatRes.json();
        }
      } catch (e) {
        console.warn('Could not fetch invoice categories:', e);
      }
      
      try {
        const generalCatRes = await fetchWithAuth(`/api/general-expenses/summary/by-category?startDate=${start}&endDate=${end}`);
        if (generalCatRes.ok) {
          generalCatData = await generalCatRes.json();
        }
      } catch (e) {
        console.warn('Could not fetch general expense categories:', e);
      }
      
      // Combine categories
      const combined = {};
      [...(Array.isArray(invoiceCatData) ? invoiceCatData : []), ...(Array.isArray(generalCatData) ? generalCatData : [])].forEach(item => {
        if (combined[item.category_name]) {
          combined[item.category_name] += item.total_amount;
        } else {
          combined[item.category_name] = item.total_amount;
        }
      });
      
      const combinedCategories = Object.entries(combined).map(([name, value]) => ({
        category_name: name,
        total_amount: value
      }));
      
      setCategoryExpenses(combinedCategories);

      // Fetch supplier category breakdown
      try {
        const supCatRes = await fetchWithAuth(`/api/supplier-invoices/summary/by-supplier-category?startDate=${start}&endDate=${end}`);
        if (supCatRes.ok) {
          const supCatData = await supCatRes.json();
          setSupplierCategoryExpenses(Array.isArray(supCatData) ? supCatData : []);
        }
      } catch (e) {
        console.warn('Could not fetch supplier category breakdown:', e);
      }

      // Calculate payroll (from payments page logic)
      await calculatePayroll(start, end);
    } catch (error) {
      console.error('❌ Error fetching financial data:', error);
      alert('Error loading data: ' + error.message);
    }
  };

  const calculatePayroll = async (start, end) => {
    try {
      console.log('🚀 Fetching payroll for exact date range:', { start, end });
      
      // Helper to get Monday of a given date
      const getMonday = (dateStr) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const dayOfWeek = date.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday = 1
        const monday = new Date(year, month - 1, day + diff);
        return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
      };

      // Helper to add days to a date string
      const addDays = (dateStr, days) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day + days);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      };

      // Helper to calculate actual shift date from week_start and day
      // Backend: 0=Sunday, 1=Monday, 2=Tuesday, ..., 6=Saturday
      // week_start is always Monday
      const getShiftDate = (weekStart, dayIndex) => {
        const dayNum = parseInt(dayIndex);
        // day=1 (Monday) → +0 days (week_start is Monday)
        // day=2 (Tuesday) → +1 day
        // day=6 (Saturday) → +5 days
        // day=0 (Sunday) → +6 days
        let daysToAdd = dayNum - 1;
        if (dayNum === 0) {
          daysToAdd = 6; // Sunday is 6 days after Monday
        }
        return addDays(weekStart, daysToAdd);
      };

      // Calculate all weeks in the date range
      const firstMonday = getMonday(start);
      const lastMonday = getMonday(end);
      
      // Collect all week starts
      const weekStarts = [];
      let currentWeek = firstMonday;
      while (currentWeek <= lastMonday) {
        weekStarts.push(currentWeek);
        currentWeek = addDays(currentWeek, 7);
      }
      
      console.log('📅 Weeks to fetch:', weekStarts);
      
      // Fetch shifts for all relevant weeks
      const shiftsRes = await fetchWithAuth(`/api/shifts?start_date=${start}&end_date=${end}`);
      const allShifts = await shiftsRes.json();
      
      console.log('📦 Total shifts fetched:', allShifts.length);
      
      // Filter shifts to only include those within the EXACT date range
      const filteredShifts = allShifts.filter(shift => {
        const shiftDate = getShiftDate(shift.week_start, shift.day);
        return shiftDate >= start && shiftDate <= end;
      });
      
      console.log('🎯 Shifts within exact date range:', filteredShifts.length);
      
      // Calculate payroll from filtered shifts
      let total = 0;
      
      filteredShifts.forEach(shift => {
        if (shift.start_time && shift.end_time) {
          const hours = calculateHours(shift.start_time, shift.end_time);
          const wage = shift.hourly_wage || shift.employee?.hourly_wage || 0;
          total += hours * wage;
        }
      });
      
      // Period paid total (allocations per week in range)
      let paid = 0;
      for (const weekStart of weekStarts) {
        const salariesRes = await fetchWithAuth(`/api/salaries?week=${weekStart}`);
        const salariesData = await salariesRes.json();
        
        salariesData.forEach(emp => {
          paid += emp.paidAmount || 0;
        });
      }

      // Actual outstanding payroll debt — same source as /payments-v2 (credit carry-forward included)
      const weekStartForDebt = getMonday(end);
      let actualUnpaid = 0;
      try {
        const cumulativeRes = await fetchWithAuth(`/api/employee-payments/cumulative/${weekStartForDebt}`);
        if (cumulativeRes.ok) {
          const cumulativeData = await cumulativeRes.json();
          if (Array.isArray(cumulativeData)) {
            actualUnpaid = cumulativeData.reduce(
              (sum, emp) => sum + (emp.cumulative_owed || 0),
              0
            );
          }
        }
      } catch (e) {
        console.warn('Could not fetch cumulative payroll debt:', e);
      }
      
      console.log('Calculated payroll:', {
        periodTotal: total.toFixed(2),
        periodPaid: paid.toFixed(2),
        actualUnpaid: actualUnpaid.toFixed(2),
      });
      setPayroll({ total, paid, unpaid: actualUnpaid });
    } catch (error) {
      console.error('Error calculating payroll:', error);
      setPayroll({ total: 0, paid: 0, unpaid: 0 });
    }
  };

  const calculateHours = (start, end) => {
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    let startMinutes = startH * 60 + startM;
    let endMinutes = endH * 60 + endM;
    
    // Handle overnight shifts (e.g., 23:00 to 02:00)
    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60; // Add 24 hours
    }
    
    return (endMinutes - startMinutes) / 60;
  };

  const formatCurrency = (amount) => {
    return `€${parseFloat(amount || 0).toFixed(2)}`;
  };

  const totalSupplierExpenses = invoiceExpenses.total_amount || 0;
  const totalGeneralExpenses = generalExpenses.total_amount || 0;
  const totalExpenses = totalSupplierExpenses + totalGeneralExpenses + (payroll.total || 0);
  const netProfit = (revenue.total_revenue || 0) - totalExpenses;
  const profitMargin = revenue.total_revenue ? ((netProfit / revenue.total_revenue) * 100).toFixed(1) : 0;

  // Prepare chart data
  const summaryData = [
    { name: 'Έσοδα', value: revenue.total_revenue || 0, color: '#00C49F' },
    { name: 'Τιμολόγια Προμηθευτών', value: totalSupplierExpenses, color: '#FF8042' },
    { name: 'Γενικά Έξοδα', value: totalGeneralExpenses, color: '#FF6B6B' },
    { name: 'Μισθοδοσία', value: payroll.total || 0, color: '#FFBB28' },
    { name: 'Καθαρό Κέρδος', value: netProfit, color: netProfit >= 0 ? '#0088FE' : '#FF0000' }
  ];

  const revenueBreakdown = [
    { name: 'Μετρητά', value: revenue.total_cash || 0 },
    { name: 'Κάρτα', value: revenue.total_card || 0 }
  ];

  const expenseBreakdown = categoryExpenses.map(cat => ({
    name: cat.category_name || 'Χωρίς Κατηγορία',
    value: cat.total_amount || 0
  }));

  // Add payroll to expense breakdown
  if (payroll.total > 0) {
    expenseBreakdown.push({
      name: 'Μισθοδοσία',
      value: payroll.total
    });
  }

  const supplierCategoryBreakdown = supplierCategoryExpenses
    .filter(cat => cat.total_amount > 0)
    .map(cat => ({
      name: cat.category_name,
      value: cat.total_amount,
      color: cat.color || '#6B7280',
      count: cat.invoice_count
    }));

  // Access denied if user can't view financial overview
  if (!canView) {
    return <AccessDenied message="Δεν έχετε πρόσβαση στην οικονομική εικόνα." />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={el}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Οικονομική Εικόνα
        </Typography>

        {/* Date Filters */}
        <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <DateRangePickerField
                startDate={startDate}
                endDate={endDate}
                onRangeChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                isMobile={isMobile}
                label="Περίοδος"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)' }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                      Συνολικά Έσοδα
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, my: 1, color: 'white' }}>
                      {formatCurrency(revenue.total_revenue)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Μετρητά: {formatCurrency(revenue.total_cash)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Κάρτα: {formatCurrency(revenue.total_card)}
                    </Typography>
                  </div>
                  <TrendingUp sx={{ fontSize: 56, opacity: 0.25 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
              color: 'white',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(249, 115, 22, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(249, 115, 22, 0.4)' }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                      Συνολικά Έξοδα
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, my: 1, color: 'white' }}>
                      {formatCurrency(totalExpenses)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Προμηθευτές: {formatCurrency(totalSupplierExpenses)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Γενικά: {formatCurrency(totalGeneralExpenses)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Μισθοδοσία: {formatCurrency(payroll.total)}
                    </Typography>
                  </div>
                  <TrendingDown sx={{ fontSize: 56, opacity: 0.25 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ 
              background: netProfit >= 0 
                ? 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              borderRadius: 3,
              boxShadow: netProfit >= 0 
                ? '0 8px 32px rgba(6, 182, 212, 0.3)'
                : '0 8px 32px rgba(239, 68, 68, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-4px)' }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                      Καθαρό Κέρδος
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, my: 1, color: 'white' }}>
                      {formatCurrency(netProfit)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Περιθώριο: {profitMargin}%
                    </Typography>
                  </div>
                  <AccountBalance sx={{ fontSize: 56, opacity: 0.25 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
              color: 'white',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(234, 179, 8, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(234, 179, 8, 0.4)' }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                      Εκκρεμή Έξοδα
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, my: 1, color: 'white' }}>
                      {formatCurrency((invoiceExpenses.pending_amount || 0) + (generalExpenses.pending_amount || 0) + (payroll.unpaid || 0))}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Τιμολόγια: {formatCurrency(invoiceExpenses.pending_amount)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Γενικά: {formatCurrency(generalExpenses.pending_amount)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Μισθοδοσία: {formatCurrency(payroll.unpaid)}
                    </Typography>
                  </div>
                  <Receipt sx={{ fontSize: 56, opacity: 0.25 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          {/* Summary Bar Chart */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#334155' }}>
                Συγκεντρωτική Εικόνα
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={summaryData} margin={{ top: 20, right: 20, bottom: 60, left: 20 }}>
                  <GradientDefs />
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis 
                    dataKey="name" 
                    angle={-20} 
                    textAnchor="end" 
                    height={80} 
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickFormatter={(value) => `€${value}`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {summaryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Revenue Breakdown Donut */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#334155' }}>
                Ανάλυση Εσόδων
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <GradientDefs />
                  <Pie
                    data={revenueBreakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    strokeWidth={0}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                  >
                    {revenueBreakdown.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % COLORS.length]}
                        style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.1))' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Expenses by Category */}
          <Grid item xs={12}>
            <Paper sx={{ 
              p: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#334155' }}>
                Ανάλυση Εξόδων ανά Κατηγορία
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={expenseBreakdown} margin={{ top: 20, right: 30, bottom: 60, left: 20 }}>
                  <GradientDefs />
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis 
                    dataKey="name" 
                    angle={-20} 
                    textAnchor="end" 
                    height={80} 
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickFormatter={(value) => `€${value}`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Bar dataKey="value" name="Έξοδα" radius={[8, 8, 0, 0]}>
                    {expenseBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Supplier Category Breakdown */}
          {supplierCategoryBreakdown.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#334155' }}>
                  Έξοδα ανά Κατηγορία Προμηθευτή
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <GradientDefs />
                    <Pie
                      data={supplierCategoryBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                    >
                      {supplierCategoryBreakdown.map((entry, index) => (
                        <Cell 
                          key={`cell-sup-${index}`} 
                          fill={entry.color || COLORS[index % COLORS.length]}
                          style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.1))' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        formatCurrency(value),
                        `${props.payload.name} (${props.payload.count} τιμολόγια)`
                      ]}
                      contentStyle={{
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Legend with amounts */}
                <Box sx={{ mt: 2 }}>
                  {supplierCategoryBreakdown.map((cat, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cat.color || COLORS[index % COLORS.length] }} />
                        <Typography variant="body2">{cat.name}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>{formatCurrency(cat.value)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Expense Category Breakdown (Donut) */}
          {expenseBreakdown.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#334155' }}>
                  Έξοδα ανά Κατηγορία Εξόδου
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <GradientDefs />
                    <Pie
                      data={expenseBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                    >
                      {expenseBreakdown.map((entry, index) => (
                        <Cell 
                          key={`cell-exp-${index}`} 
                          fill={COLORS[index % COLORS.length]}
                          style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.1))' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {/* Quick Stats Table */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Αναλυτικά Στοιχεία
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Συνολικά Έσοδα:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      {formatCurrency(revenue.total_revenue)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}><Divider /></Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Τιμολόγια Προμηθευτών:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold" color="error.main">
                      {formatCurrency(totalSupplierExpenses)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Γενικά Έξοδα:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold" color="error.main">
                      {formatCurrency(totalGeneralExpenses)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Μισθοδοσία:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold" color="error.main">
                      {formatCurrency(payroll.total)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}><Divider /></Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Σύνολο Εξόδων:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold" color="error.main">
                      {formatCurrency(totalExpenses)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}><Divider /></Grid>

                  <Grid item xs={6}>
                    <Typography variant="h6">Καθαρό Κέρδος:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography 
                      variant="h6" 
                      fontWeight="bold" 
                      color={netProfit >= 0 ? 'success.main' : 'error.main'}
                    >
                      {formatCurrency(netProfit)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Περιθώριο Κέρδους:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography 
                      variant="body1" 
                      fontWeight="bold"
                      color={profitMargin >= 0 ? 'success.main' : 'error.main'}
                    >
                      {profitMargin}%
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Payment Status */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Κατάσταση Πληρωμών Εξόδων
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Τιμολόγια Προμηθευτών:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(invoiceExpenses.total_amount)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Γενικά Έξοδα:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(generalExpenses.total_amount)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}><Divider /></Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="success.main">Πληρωμένα:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      {formatCurrency((invoiceExpenses.paid_amount || 0) + (generalExpenses.paid_amount || 0) + (payroll.paid || 0))}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="warning.main">Εκκρεμή:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold" color="warning.main">
                      {formatCurrency((invoiceExpenses.pending_amount || 0) + (generalExpenses.pending_amount || 0) + (payroll.unpaid || 0))}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}><Divider /></Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Μισθοδοσία Πληρωμένη:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      {formatCurrency(payroll.paid || 0)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Μισθοδοσία Εκκρεμής:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold" color="warning.main">
                      {formatCurrency(payroll.unpaid || 0)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}><Divider /></Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Ποσοστό Πληρωμένων:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      {((invoiceExpenses.total_amount || 0) + (generalExpenses.total_amount || 0) + (payroll.total || 0)) > 0 
                        ? ((((invoiceExpenses.paid_amount || 0) + (generalExpenses.paid_amount || 0) + (payroll.paid || 0)) / ((invoiceExpenses.total_amount || 0) + (generalExpenses.total_amount || 0) + (payroll.total || 0))) * 100).toFixed(1)
                        : 0}%
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}
